import React, { useState, useEffect } from 'react'
import {Users, Baby, GraduationCap, Calendar, Download} from 'lucide-react'
import { motion } from 'framer-motion'
import { lumi } from '../lib/lumi'

interface BibleSchoolClass {
  _id: string
  title: string
  category: 'ADULTS' | 'YOUTH' | 'KIDS'
  teacher: string
  schedule: string
  description?: string
  startDate?: string
  endDate?: string
  maxStudents?: number
  materialsUrl?: string
}

const BibleSchool: React.FC = () => {
  const [classes, setClasses] = useState<BibleSchoolClass[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const { list } = await lumi.entities.bible_school_classes.list({
          sort: { category: 1 }
        })
        setClasses(list)
      } catch (error) {
        console.error('Erro ao carregar turmas:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchClasses()
  }, [])

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'ADULTS': return GraduationCap
      case 'YOUTH': return Users
      case 'KIDS': return Baby
      default: return GraduationCap
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'ADULTS': return 'bg-[#1e3a8a]'
      case 'YOUTH': return 'bg-[#6b8f71]'
      case 'KIDS': return 'bg-[#1e3a8a]'
      default: return 'bg-[#1e3a8a]'
    }
  }

  const materials = [
    { id: '1', title: 'Apostila - Fundamentos da Fé', size: '2.5 MB' },
    { id: '2', title: 'Guia de Estudo Bíblico', size: '1.8 MB' },
    { id: '3', title: 'Cronograma 2024', size: '850 KB' }
  ]

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#1e3a8a] to-[#6b8f71] py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl font-bold text-white mb-6">
              Escola Bíblica
            </h1>
            <p className="text-xl text-white/90">
              Cresça no conhecimento da Palavra de Deus
            </p>
          </motion.div>
        </div>
      </section>

      {/* Turmas */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-[#1e3a8a] mb-12 text-center">
            Nossas Turmas
          </h2>
          {loading ? (
            <div className="text-center py-12 text-gray-600">
              Carregando turmas...
            </div>
          ) : classes.length === 0 ? (
            <div className="text-center py-12 text-gray-600">
              Nenhuma turma disponível no momento.
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {classes.map((classItem, index) => {
                const Icon = getCategoryIcon(classItem.category)
                const colorClass = getCategoryColor(classItem.category)
                return (
                  <motion.div
                    key={classItem._id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all overflow-hidden"
                  >
                    <div className={`${colorClass} p-6 text-white`}>
                      <Icon className="mx-auto mb-4" size={48} />
                      <h3 className="text-2xl font-bold text-center">
                        {classItem.title}
                      </h3>
                    </div>
                    <div className="p-6">
                      <p className="text-gray-700 mb-4">
                        {classItem.description || 'Estudo bíblico aprofundado'}
                      </p>
                      <div className="flex items-center gap-2 text-[#6b8f71] font-semibold mb-6">
                        <Calendar size={20} />
                        {classItem.schedule}
                      </div>
                      <button className="w-full bg-[#6b8f71] text-white py-3 rounded-lg font-semibold hover:bg-[#5a7760] transition-all">
                        Fazer Matrícula
                      </button>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {/* Materiais */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-[#1e3a8a] mb-12 text-center">
            Materiais de Estudo
          </h2>
          <div className="space-y-4">
            {materials.map((material, index) => (
              <motion.div
                key={material.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-[#1e3a8a] w-12 h-12 rounded-lg flex items-center justify-center">
                    <Download className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a8a]">
                      {material.title}
                    </h3>
                    <p className="text-sm text-gray-500">{material.size}</p>
                  </div>
                </div>
                <button className="bg-[#6b8f71] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#5a7760] transition-all">
                  Baixar
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default BibleSchool
